import { AutocompleteSearchType, IntegrationException, Location, TopicCategory, UserTag } from '../../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { fetchPlacePredictionsAsync, geocodePlaceIdAsync, PlacePredictionLite, smoothZoom } from '../../helpers/api/google-api';
import { isDefined, setMarkerMap, updateMarkerVisibility } from '../../helpers/utils';

import { Marker } from '@twitchaxorg/markerclusterer';
import pointCache from '../../helpers/state/point-cache';
import topicData from '../../helpers/state/topic-data';

interface SearchValue {
    name: string;
    value: any;
}

const AUTOCOMPLETE_DEBOUNCE_MS = 275;

@customElement('proxi-autocomplete')
export class AutocompleteComponent extends LitElement {

    @state()
    termToPointName: Map<string, string> = new Map();

    @state()
    pointNameToId: Map<string, string> = new Map();

    @query('#autocomplete')
    autocompleteInput: HTMLInputElement | undefined;

    @property({ type: Boolean })
    isInFilterChip: boolean = false;

    @state()
    selectedPointName: string | undefined;

    @state()
    inputTerm: string = '';

    @state()
    sessionToken: google.maps.places.AutocompleteSessionToken | undefined;

    @state()
    googlePlaces: PlacePredictionLite[] = [];

    @state()
    geocoder: google.maps.Geocoder | undefined;

    @state()
    categories: TopicCategory[] = [];

    @state()
    tags: UserTag[] = [];

    @state()
    searchLimit: number = 3;

    @state()
    showMoreButton: boolean = true;

    @state()
    hideDropdown: boolean = true;

    @state()
    searchTypes: AutocompleteSearchType[] = [];


    @state()
    temporaryIcon?: Marker;

    @state()
    temporaryRectangle?: google.maps.Rectangle;

    private predictionDebounce?: number;

    protected firstUpdated() {
        if (this.searchTypes.length === 0) {
            this.searchTypes = [AutocompleteSearchType.GoogleSearch, AutocompleteSearchType.Point];
        }
        this.resetSearch();
        this.setSearchables();
    }

    // if searchtypes changes, reset the searchables
    protected updated(changedProperties: Map<string, any>) {
        if (changedProperties.has('searchTypes') && this.searchTypes.length > 0) {
            // if it
            this.setSearchables();
        }
    }

    // disconnectedCallback() removes the autocomplete service and session token and the temporary icon
    disconnectedCallback() {
        if (this.predictionDebounce !== undefined) {
            window.clearTimeout(this.predictionDebounce);
        }
        if (this.sessionToken) {
            this.sessionToken = undefined;
        }
        if (this.temporaryIcon) {
            setMarkerMap(this.temporaryIcon, null);
            updateMarkerVisibility(this.temporaryIcon, false);
            this.temporaryIcon = undefined;
        }
        if (this.temporaryRectangle) {
            this.temporaryRectangle.setMap(null);
            this.temporaryRectangle = undefined;
        }
        this.hideDropdown = true;
        this.googlePlaces = [];
        this.inputTerm = '';
    }

    private resetSearch() {
        if (isDefined(this.autocompleteInput)) {
            this.autocompleteInput.value = '';
            this.inputTerm = '';
        }
        this.requestUpdate();
    }

    setSearchables() {
        if (this.searchTypes.includes(AutocompleteSearchType.Point)) {
            this.termToPointName = new Map(
                pointCache.seearchableTerms.flatMap(pointTerm =>
                    pointTerm.terms.map(term => [term, pointTerm.point.name ?? pointTerm.point.location.search] as [string, string])
                ).filter(([term, pointName]) => pointName !== undefined)
            );
            this.pointNameToId = new Map(
                pointCache.seearchableTerms.map(pointTerm =>
                    [pointTerm.point.name ?? pointTerm.point.location.search, pointTerm.point._id?.$oid] as [string, string]
                )
            );
        }
        if (this.searchTypes.includes(AutocompleteSearchType.Category)) {
            this.categories = topicData.categories;
        }
        if (this.searchTypes.includes(AutocompleteSearchType.Tag)) {
            this.tags = topicData.allUserTags;
        }
    }

    initializeService() {
        if (!this.sessionToken) {
            this.sessionToken = new google.maps.places.AutocompleteSessionToken();
        }
    }

    async onInput(event: Event) {
        const input = event.target as HTMLInputElement;
        this.inputTerm = input.value;
        if (this.inputTerm.length > 0 && (this.searchTypes.includes(AutocompleteSearchType.GoogleSearch))) {
            this.initializeService();
            if (this.predictionDebounce !== undefined) {
                window.clearTimeout(this.predictionDebounce);
            }
            this.predictionDebounce = window.setTimeout(async () => {
                await this.googlePlaceSearch(this.inputTerm);
            }, AUTOCOMPLETE_DEBOUNCE_MS);
        }
        this.hideDropdown = false;
    }

    async googlePlaceSearch(term: string) {
        // limit to zip codes if the search type is ZipCode
        let types: string[] | undefined;
        if ((topicData.topic?.search_pill_settings?.search_filters?.length ?? 0) >0) {
            types = topicData.topic?.search_pill_settings?.search_filters;
        }

        this.googlePlaces = await fetchPlacePredictionsAsync({
            input: term,
            sessionToken: this.sessionToken,
            locationBias: pointCache.map?.getBounds(),
            types,
        });
        this.requestUpdate();
    }

    limitSearch(type: AutocompleteSearchType) {
        this.searchTypes = [type];
        this.searchLimit = 15;
        this.showMoreButton = false;
    }

    // Actions for selecting a search result
    private async geocodePlace(place_id: string): Promise<google.maps.GeocoderResult | undefined>{
        const result = await geocodePlaceIdAsync(place_id);
        return result[0];
    }

    private async selectGooglePlace(place: SearchValue) {
        const result = await this.geocodePlace(place.value.place_id);
        if (isDefined(result)) {
            this.centerMapOnPoint(result);
        }
        this.sessionToken = undefined;
        this.resetSearch();
    }

    private async addToMap(results: google.maps.GeocoderResult) {
        if (!pointCache.map) {
            return;
        }

        const location: Location = {
            place_id: results.place_id,
            latLng: {
                lat: results.geometry.location.lat(),
                lng: results.geometry.location.lng()
            },
            search: results.formatted_address,
        };


        // add the point to the map
        this.dispatchEvent(
            new CustomEvent('add-location-to-map', {
                detail: {
                    location
                },
                bubbles: true,
                composed: true
            })
        );

        // remove the temporary icon if it exists
        if (this.temporaryIcon) {
            setMarkerMap(this.temporaryIcon, null);
            updateMarkerVisibility(this.temporaryIcon, false);
            this.temporaryIcon = undefined;
        }
        // remove the temporary rectangle if it exists
        if (this.temporaryRectangle) {
            this.temporaryRectangle.setMap(null);
            this.temporaryRectangle = undefined;
        }

        this.resetSearch();
    }


    private centerMapOnPoint(results: google.maps.GeocoderResult) {
        if (!pointCache.map) {
            return;
        }
        pointCache.map?.panTo(results.geometry.location);
        pointCache.map?.panToBounds(results.geometry.viewport);
        const defaultZoom = topicData.topic?.default_map_zoom ?? pointCache.map?.getZoom() ?? 10;
        const currentZoom = pointCache.map.getZoom() ?? defaultZoom;
        const zoom_change =
            topicData.hasExceptions === IntegrationException.TheGoSolution || topicData.hasExceptions === IntegrationException.Telco ? 7 :
            topicData.hasExceptions === IntegrationException.DefHero ? 0 : 0;
        if (currentZoom < defaultZoom + zoom_change && zoom_change > 0) {
            void smoothZoom(pointCache.map, defaultZoom + zoom_change, currentZoom);
        }
        // add a point to the map with geojosn
        const icon: google.maps.Icon = {
            url: 'https://img.icons8.com/?size=60&id=aN7gdH3fNz94&format=png',
            scaledSize: new google.maps.Size(40, 40),
            anchor: new google.maps.Point(20, 20),
        };
        if (topicData.hasExceptions === IntegrationException.DefHero) {
            // add a 100 mile radius circle around the point
            const radius = 160934; // 100 miles in meters
            const circle = new google.maps.Circle({
                strokeColor: '#000000',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                zIndex: 0,
                map: pointCache.map,
                center: results.geometry.location,
                radius
            });

            // add to map
            circle.setMap(pointCache.map);
            // set map to fit circle
            const bounds = circle.getBounds();
            if (bounds !== null) {
                pointCache.map.fitBounds(bounds);
            }
        } else {
            const marker = new google.maps.Marker({
                position: results.geometry.location,
                map: pointCache.map,
                title: results.formatted_address,
                icon,
                // Add animation to draw attention
                animation: google.maps.Animation.DROP
            });

            if (topicData.topic?.search_pill_settings?.allow_add === true && topicData.isAddPointer) {
                // Create an info window with "Click to add to map" text
                const infoWindow = new google.maps.InfoWindow({
                    content: `
                        <div style="text-align: center; padding: 5px;">
                            <strong>${results.formatted_address}</strong><br>
                            <span style="color: var(--main-moon-color); font-size: 0.9em;">Click pin to add to map</span>
                        </div>
                    `,

                });

                // Open the info window immediately
                infoWindow.open(pointCache.map, marker);

                // Add click listener to the marker
                marker.addListener('click', async () => {
                    await this.addToMap(results);
                    // Provide visual feedback that the marker was clicked
                    marker.setAnimation(google.maps.Animation.BOUNCE);
                    setTimeout(() => marker.setAnimation(null), 1500);

                    // Close the info window after click
                    infoWindow.close();
                });
            }

            // add marker to map
            setMarkerMap(marker, pointCache.map);
            // set marker visibility
            updateMarkerVisibility(marker, true);
            // set marker as temporary icon
            if (this.temporaryIcon) {
                setMarkerMap(this.temporaryIcon, null);
            }
            this.temporaryIcon = marker;
            // set marker as temporary rectangle
            if (this.temporaryRectangle) {
                this.temporaryRectangle.setMap(null);
            }
            // set marker as temporary rectangle
            const rectangle = new google.maps.Rectangle({
                bounds: results.geometry.bounds,
                strokeColor: 'red',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: 'red',
                fillOpacity: 0,
                zIndex: 0,
            });
            // add rectangle to map
            rectangle.setMap(pointCache.map);
            // set rectangle as temporary rectangle
            this.temporaryRectangle = rectangle;

            // set map to zoom to rectangle bounds
            const bounds = rectangle.getBounds();
            if (bounds !== null) {
                pointCache.map.fitBounds(bounds);
            }
        }

        this.resetSearch();
    }


    private selectPoint(point: SearchValue) {
        this.googlePlaces = [];
        if (this.autocompleteInput) {
            this.autocompleteInput.value = '';
        }
        // get from pointNameToId
        const pointId = this.pointNameToId.get(point.value);
        if (pointId) {
            this.dispatchEvent(
                new CustomEvent('on-select-point', {
                    detail: pointId,
                })
            );
        }

        this.resetSearch();
    }

    selectCategory(category: SearchValue) {
        this.googlePlaces = [];
        if (this.autocompleteInput) {
            this.autocompleteInput.value = '';
        }

        this.dispatchEvent(
            new CustomEvent('on-select-category', {
                detail: category.value.name
            })
        );
    }

    selectTag(tag: SearchValue) {

        this.resetSearch();

        if (this.autocompleteInput) {
            this.autocompleteInput.value = '';
        }

        this.dispatchEvent(
            new CustomEvent('on-select-tag', {
                detail: tag.value.name
            })
        );
    }

    closeSearch() {
        this.resetSearch();
        if (this.temporaryRectangle) {
            this.temporaryRectangle.setMap(null);
            this.temporaryRectangle = undefined;
        }
        if (this.temporaryIcon) {
            setMarkerMap(this.temporaryIcon, null);
            updateMarkerVisibility(this.temporaryIcon, false);
            this.temporaryIcon = undefined;
        }
        this.dispatchEvent( new CustomEvent('close-search'));
    }

    keywordSearch(value: SearchValue) {
        this.googlePlaces = [];
        if (this.autocompleteInput) {
            this.autocompleteInput.value = '';
        }
        // dispatch a custom event with the keyword
        this.dispatchEvent(
            new CustomEvent('on-keyword-search', {
                detail: value.value,
            })
        );

        // close the search
        this.resetSearch();
    }

    async optionClick(type: AutocompleteSearchType, value: SearchValue) {
        if (type === AutocompleteSearchType.GoogleSearch) {
            await this.selectGooglePlace(value);
        } else if (type === AutocompleteSearchType.Point) {
            this.selectPoint(value);
        } else if (type === AutocompleteSearchType.Category) {
            this.selectCategory(value);
        } else if (type === AutocompleteSearchType.Tag) {
            this.selectTag(value);
        } else if (type === AutocompleteSearchType.Keyword) {
            this.keywordSearch(value);
        }
        // hide the dropdown
        this.resetDropdown();
    }

    resetDropdown() {
        this.hideDropdown = true;
        if (this.autocompleteInput) {
            this.autocompleteInput.value = '';
            this.inputTerm = '';
        }
        this.googlePlaces = [];
        this.requestUpdate();
    }

    render() {
        const termToCount = new Map<string, number>();
        Array.from(this.termToPointName.keys()).forEach(term => {
            if (term.toLowerCase().includes(this.inputTerm.toLowerCase())) {
                const pointName = this.termToPointName.get(term);
                if (pointName) {
                    termToCount.set(pointName, ((termToCount.get(pointName) ?? 0) || 0) + 1);
                }
            } else if (this.inputTerm.length === 0) {
                const pointName = this.termToPointName.get(term);
                if (pointName) {
                    termToCount.set(pointName, ((termToCount.get(pointName) ?? 0) || 0) + 1);
                }
            }
        });

        const uniquePointNames = Array.from(termToCount.keys());
        uniquePointNames.sort((a, b) => ((termToCount.get(b) ?? 0) || 0) - ((termToCount.get(a) ?? 0) || 0));
        const uniquePointValues = uniquePointNames.map(pointName => ({ name: pointName, value:  pointName}));

        const categories: SearchValue[] = this.categories
            .filter(category => category.name.toLowerCase().includes(this.inputTerm.toLowerCase()))
            .map(category => ({ name: category.name, value: category }));

        const tags: SearchValue[] = this.tags
            .filter(tag => tag.name.toLowerCase().includes(this.inputTerm.toLowerCase()))
            .map(tag => ({ name: tag.name, value: tag }));

        const keyword: SearchValue[] = [{ name: this.inputTerm, value: this.inputTerm }];

        const googlePlaceValues: SearchValue[] = this.googlePlaces.map(place => ({ name: place.description, value: place }));
        const showDropdown = !this.hideDropdown && (this.autocompleteInput?.value.length ?? 0) > 0;
        const showPointSearch = this.searchTypes.includes(AutocompleteSearchType.Point) && uniquePointNames.length > 0;
        const showCategorySearch = this.searchTypes.includes(AutocompleteSearchType.Category) && categories.length > 0;
        const showTagSearch = this.searchTypes.includes(AutocompleteSearchType.Tag) && tags.length > 0;
        const showGooglePlaces = this.googlePlaces.length > 0;
        const showKeywordSearch = this.searchTypes.includes(AutocompleteSearchType.Keyword) && this.inputTerm.length > 0;

        // Create placeholder text dynamically based on what types are available
        let placeholderText = 'Search for';
        if (topicData.isAddPointer && topicData.topic?.search_pill_settings?.pill_text) {
            placeholderText = topicData.topic.search_pill_settings.pill_text;
        } else {
            for (let i = 0; i < this.searchTypes.length; i++) {
                const search = this.searchTypes[i];
                placeholderText += `${i !== 0 ? ',' : ''} ${search.toString()}s`;
            }
        }

        return html`
            <div  class="filter_chip_search ${showDropdown && !this.isInFilterChip ? 'autocomplete-with-content' : ''} " style="${this.isInFilterChip && showDropdown ? 'border-radius: 16px 16px 0 0 !important;' : ''}">

                <span class="icon-end" @click="${() => this.resetDropdown()}">
                    <mwc-icon style="--mdc-icon-size: 16px;margin-top:2px">
                        search
                    </mwc-icon>
                </span>
                <input
                    id="autocomplete"
                    @input="${this.onInput}"
                    @focus="${(e: FocusEvent) => {
                        this.hideDropdown = false;
                        (e.target as HTMLInputElement).focus({ preventScroll: true });
                    }}"
                    type="text"
                    placeholder="${placeholderText}"
                />
                <span class="icon-end" @click="${() => this.closeSearch()}" style="cursor: pointer;">
                    <mwc-icon style="--mdc-icon-size: 16px; margin-right: 8px; margin-left: 0px; margin-top:2px">
                        close
                    </mwc-icon>
                </span>
            </div>
            ${showDropdown ? html`
                <div id="dropdown" class="dropdown ${this.isInFilterChip ? 'dropdown_chips' : ''}">
                    ${this.autocompleteInput ? html`
                        ${this.renderDropdownSection(showPointSearch, uniquePointValues, `${topicData.topic?.point_name ?? 'Points'}`, AutocompleteSearchType.Point)}
                        ${this.autocompleteInput.value.length > 0 ? html`
                            ${this.renderDropdownSection(showGooglePlaces, googlePlaceValues, 'All Places', AutocompleteSearchType.GoogleSearch)}
                        ` : ''}
                        ${this.renderDropdownSection(showCategorySearch, categories, `${topicData.topic?.add_point_customization?.category?.header ?? 'Categories'}`, AutocompleteSearchType.Category)}
                        ${this.renderDropdownSection(showTagSearch, tags, `${topicData.topic?.add_point_customization?.tags?.header ?? 'Tags'}`, AutocompleteSearchType.Tag)}
                        ${this.renderDropdownSection(showKeywordSearch, keyword, 'Search Keyword', AutocompleteSearchType.Keyword)}
                    ` : ''}

                </div>
            ` : ''}
        `;
    }

    getIconForType(type: AutocompleteSearchType, value: SearchValue): string {
        switch (type) {
            case AutocompleteSearchType.GoogleSearch:
                return '🔍'; // Replace with actual Google icon URL
            case AutocompleteSearchType.Point:
                return topicData.categories.find(c => c._id?.$oid === value.value._id?.$oid)?.icon ?? '🔍'; // Replace with actual Point icon URL
            case AutocompleteSearchType.Tag:
                return '🔍'; // Replace with actual Tag icon URL
            default:
                return '🔍'; // Replace with a default icon URL
        }
    }

    renderDropdownSection(showSection: boolean, items: SearchValue[], sectionHeader: string, type: AutocompleteSearchType){
        return showSection ? html`
            ${!this.isInFilterChip ? html`
                <hr />
            ` : ''}
            ${this.searchTypes.length > 1 ? html`
                <span class="dropdown-section-header">
                    <span class="section-name">${sectionHeader} <i>${items.length}</i></span>
                    ${this.showMoreButton && this.searchTypes.length > 5 ? html`<span class="more-button ${type === AutocompleteSearchType.GoogleSearch ? 'hidden' : ''}"
                        @click=${() => this.limitSearch(type)}>More</span>` : ''}
                </span>
            ` : ''}
            ${items.slice(0, this.searchLimit).map(
                item =>
                html`
                    <div
                        class="dropdown-item"
                        @click="${async () => await this.optionClick(type, item)}"
                    >
                        <span class="icon">${this.getIconForType(type, item)}</span>
                        <span class="result-text">${item.name}</span>
                    </div>
                `
            )}
        ` : '';
    }

    static get styles() {
        return css`
            #autocomplete {
                width: 100%;
                margin: 4px 0;
                box-sizing: border-box;
                border: none !important;
                border-radius: 4px;
                cursor: text;
                height: 100%;
                font-size: 16px;
            }

            input {
                margin: 0;
            }

            #autocomplete:focus, #autocomplete:focus-visible {
                outline: none;
            }

            .filter_chip_search {
                border-radius: 16px;
                height: 2rem !important;
                margin: 0 !important;
                background-color: var(--theme-white-color);
                box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px !important;
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .autocomplete-with-content {
                border: 1px solid #ccc !important;
                border-bottom: none !important;
                border-radius: 4px 4px 0 0 !important;
                margin-bottom: 0 !important;
                background-color: white !important;
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .dropdown_chips {
                padding: 8px !important;
                background-color: var(--theme-white-color) !important;
                box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px !important;
                border: 0px solid transparent !important;
                border-radius: 0 0 16px 16px !important;
                margin-bottom: 4px;
                width: unset !important;
            }

            .dropdown {
                position: relative;
                display: inline-block;
                width: calc(100% - 2.5px);
                max-height: 200px;
                overflow: auto;
                overscroll-behavior: contain;
                border: 1px solid #ccc;
                border-radius: 0 0 4px 4px;
                border-top: none;
                background-color: white;
                color: black;
            }

            hr {
                background-color: var(--theme-lighter-text-color);
            }

            /* width */
            .dropdown::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .dropdown::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .dropdown::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .dropdown-item {
                padding: 5px 10px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            .dropdown-section-header{
                padding: 5px 10px;
                font-weight: bold;
                color: var(--theme-lighter-text-color);
                display: flex;
                justify-content: space-between;
            }

            .hidden {
                display: none;
            }

            .more-button {
                cursor: pointer;
                text-decoration: underline;
            }

            .section-name {
                flex: 1;
                text-align: left;
            }

            .result-text {
                flex: 1;
                text-align: left;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .dropdown-item:hover {
                background-color: #f1f1f1;
            }

            .icon {
                margin-right: 10px;
            }

            .icon-end {
                margin-left: 10px;
            }
        `;
    }
}
